import { useEffect, useState } from 'react'
import {
  useMicrosoft365Settings,
  useRoles,
  useRunDirectorySync,
  useSaveMicrosoft365Settings,
  useSetRoleEntraGroup,
  useTestFileAccess,
  useTestMicrosoft365
} from '../../api/queries'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { DirectorySyncResult, MailSettings, Microsoft365Settings, Role } from '../../api/types'
import { Roles } from '../../api/types'
import { Card, ErrorBox, Loading } from '../../components/ui'
import { formatDateTime } from '../../lib/format'

/** Nøgler som backenden bruger til at markere felter der er låst af environment variables. */
const LOCK_KEYS = {
  tenantId: 'entra.tenantId',
  clientId: 'entra.clientId',
  clientSecret: 'entra.clientSecret',
  allowedTenantIds: 'entra.allowedTenantIds'
}

/**
 * Microsoft 365-opsætning (kun Superadmin).
 *
 * Her kobles systemet til virksomhedens Entra ID: login for brugerne og
 * synkronisering af brugere, så oprettelser, navneskift og spærringer i
 * Microsoft 365 slår igennem i systemet.
 */
export function Microsoft365Page() {
  const { data, isLoading, error } = useMicrosoft365Settings()

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return <ErrorBox error={new Error('Indstillingerne kunne ikke hentes.')} />

  return (
    <div className="stack" style={{ gap: 16 }}>
      <StatusCard settings={data} />
      <ConnectionCard settings={data} />
      <SyncCard settings={data} />
      <FilesCard settings={data} />
      <MailCard />
      <RoleMappingCard />
    </div>
  )
}

function StatusCard({ settings }: { settings: Microsoft365Settings }) {
  return (
    <Card title="Status">
      <div className="form-grid">
        <StatusLine
          label="Microsoft-login"
          ok={settings.isLoginConfigured}
          okText="Konfigureret"
          failText="Ikke konfigureret - udfyld tenant id og klient-id"
        />
        <StatusLine
          label="Brugersynkronisering"
          ok={settings.isSyncConfigured}
          okText={settings.syncEnabled ? 'Klar og slået til' : 'Klar, men slået fra'}
          failText="Kræver også en klienthemmelighed"
        />
      </div>

      {settings.restartRequired && (
        <div className="error-box" style={{ marginTop: 12, background: '#FFF7ED', borderColor: '#FED7AA', color: '#9A3412' }}>
          Opsætningen er gemt, men den kørende server bruger stadig den gamle. Genstart API-et
          (<code>sudo systemctl restart tcpss-api</code>), før brugerne kan logge ind med den nye opsætning.
          Synkronisering virker med det samme.
        </div>
      )}

      {settings.lockedByEnvironment.length > 0 && (
        <p className="tiny muted" style={{ marginBottom: 0 }}>
          Låst af environment variables på serveren: {settings.lockedByEnvironment.join(', ')}. Felterne kan
          ikke ændres her.
        </p>
      )}
    </Card>
  )
}

function StatusLine({ label, ok, okText, failText }: { label: string; ok: boolean; okText: string; failText: string }) {
  return (
    <div>
      <div className="tiny muted">{label}</div>
      <div className="row" style={{ gap: 6 }}>
        <span className="pill" style={{ background: ok ? '#DCFCE7' : '#FEE2E2', color: ok ? '#166534' : '#991B1B' }}>
          <span className="pill__dot" />
          {ok ? okText : failText}
        </span>
      </div>
    </div>
  )
}

function ConnectionCard({ settings }: { settings: Microsoft365Settings }) {
  const save = useSaveMicrosoft365Settings()
  const test = useTestMicrosoft365()

  const [form, setForm] = useState({
    tenantId: settings.tenantId ?? '',
    clientId: settings.clientId ?? '',
    clientSecret: '',
    allowedTenantIds: settings.allowedTenantIds ?? ''
  })
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  const locked = (key: string) => settings.lockedByEnvironment.includes(key)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setMessage(null)
    try {
      await save.mutateAsync({
        ...settings,
        tenantId: form.tenantId || null,
        clientId: form.clientId || null,
        clientSecret: form.clientSecret || null,
        allowedTenantIds: form.allowedTenantIds || null
      })
      setForm((current) => ({ ...current, clientSecret: '' }))
      setMessage({ ok: true, text: 'Opsætningen er gemt.' })
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Kunne ikke gemme.' })
    }
  }

  async function runTest() {
    setMessage(null)
    const result = await test.mutateAsync()
    setMessage({ ok: result.success, text: result.message })
  }

  return (
    <Card title="Forbindelse til Microsoft 365">
      <p className="small muted" style={{ marginTop: 0 }}>
        Værdierne findes i Azure-portalen under <strong>Microsoft Entra ID → App registrations</strong>.
        Klienthemmeligheden gemmes krypteret og vises aldrig igen.
      </p>

      <form onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Tenant id (Directory ID)</span>
            <input
              value={form.tenantId}
              disabled={locked(LOCK_KEYS.tenantId)}
              placeholder="00000000-0000-0000-0000-000000000000"
              onChange={(e) => setForm({ ...form, tenantId: e.target.value })}
            />
          </label>

          <label className="field">
            <span className="field__label">Klient-id (Application ID)</span>
            <input
              value={form.clientId}
              disabled={locked(LOCK_KEYS.clientId)}
              placeholder="00000000-0000-0000-0000-000000000000"
              onChange={(e) => setForm({ ...form, clientId: e.target.value })}
            />
          </label>

          <label className="field">
            <span className="field__label">
              Klienthemmelighed {settings.hasClientSecret && <span className="muted">(gemt - udfyld kun for at skifte)</span>}
            </span>
            <input
              type="password"
              autoComplete="new-password"
              value={form.clientSecret}
              disabled={locked(LOCK_KEYS.clientSecret)}
              placeholder={settings.hasClientSecret ? '••••••••' : 'Client secret value'}
              onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
            />
          </label>

          <label className="field">
            <span className="field__label">Tilladte tenants (kommasepareret, tom = kun ovenstående)</span>
            <input
              value={form.allowedTenantIds}
              disabled={locked(LOCK_KEYS.allowedTenantIds)}
              onChange={(e) => setForm({ ...form, allowedTenantIds: e.target.value })}
            />
          </label>
        </div>

        {message && (
          <div
            className={message.ok ? 'small' : 'error-box'}
            style={{ marginBottom: 10, color: message.ok ? 'var(--success)' : undefined }}
          >
            {message.text}
          </div>
        )}

        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Gemmer ...' : 'Gem opsætning'}
          </button>
          <button className="btn" type="button" disabled={test.isPending || !settings.isSyncConfigured} onClick={() => void runTest()}>
            {test.isPending ? 'Tester ...' : 'Test forbindelse'}
          </button>
          {settings.hasClientSecret && !locked(LOCK_KEYS.clientSecret) && (
            <button
              className="btn btn--danger"
              type="button"
              disabled={save.isPending}
              onClick={async () => {
                if (!window.confirm('Fjern den gemte klienthemmelighed? Synkronisering stopper.')) return
                await save.mutateAsync({ ...settings, clientSecret: '-' })
                setMessage({ ok: true, text: 'Klienthemmeligheden er fjernet.' })
              }}
            >
              Fjern hemmelighed
            </button>
          )}
        </div>
      </form>

      <p className="tiny muted" style={{ marginBottom: 0, marginTop: 12 }}>
        Appregistreringen skal have applikationsrettigheden <code>User.Read.All</code> (og{' '}
        <code>GroupMember.Read.All</code> hvis du filtrerer på en gruppe) med administratorsamtykke.
      </p>
    </Card>
  )
}

function SyncCard({ settings }: { settings: Microsoft365Settings }) {
  const save = useSaveMicrosoft365Settings()
  const sync = useRunDirectorySync()

  const [form, setForm] = useState({
    syncEnabled: settings.syncEnabled,
    syncGroupId: settings.syncGroupId ?? '',
    syncIntervalMinutes: settings.syncIntervalMinutes,
    syncDeactivateMissing: settings.syncDeactivateMissing,
    syncDefaultRole: settings.syncDefaultRole
  })
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)
  const [result, setResult] = useState<DirectorySyncResult | null>(settings.lastSync ?? null)

  useEffect(() => {
    setResult(settings.lastSync ?? null)
  }, [settings.lastSync])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setMessage(null)
    try {
      await save.mutateAsync({ ...settings, ...form, syncGroupId: form.syncGroupId || null })
      setMessage({ ok: true, text: 'Indstillingerne er gemt.' })
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Kunne ikke gemme.' })
    }
  }

  async function runNow() {
    setMessage(null)
    const outcome = await sync.mutateAsync()
    setResult(outcome)
  }

  return (
    <Card title="Brugersynkronisering">
      <p className="small muted" style={{ marginTop: 0 }}>
        Henter brugerne fra Microsoft 365. Nye brugere oprettes, navne og e-mails opdateres, og brugere der
        slettes eller spærres i Microsoft 365 bliver <strong>deaktiveret</strong> her - aldrig slettet, så
        deres projekter, opgaver og historik bevares.
      </p>

      <form onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Automatisk synkronisering</span>
            <select
              value={form.syncEnabled ? 'true' : 'false'}
              onChange={(e) => setForm({ ...form, syncEnabled: e.target.value === 'true' })}
            >
              <option value="false">Slået fra</option>
              <option value="true">Slået til</option>
            </select>
          </label>

          <label className="field">
            <span className="field__label">Interval (minutter)</span>
            <input
              type="number"
              min={5}
              max={1440}
              value={form.syncIntervalMinutes}
              onChange={(e) => setForm({ ...form, syncIntervalMinutes: Number(e.target.value) })}
            />
          </label>

          <label className="field">
            <span className="field__label">Begræns til gruppe (object id, tom = alle brugere)</span>
            <input
              value={form.syncGroupId}
              placeholder="F.eks. gruppen Projekt-System-Brugere"
              onChange={(e) => setForm({ ...form, syncGroupId: e.target.value })}
            />
          </label>

          <label className="field">
            <span className="field__label">Rolle til nye brugere</span>
            <select value={form.syncDefaultRole} onChange={(e) => setForm({ ...form, syncDefaultRole: e.target.value })}>
              {[Roles.Apprentice, Roles.Employee, Roles.ProjectManager, Roles.Office, Roles.Administrator].map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Brugere der ikke længere findes i Microsoft 365</span>
            <select
              value={form.syncDeactivateMissing ? 'true' : 'false'}
              onChange={(e) => setForm({ ...form, syncDeactivateMissing: e.target.value === 'true' })}
            >
              <option value="true">Deaktiveres</option>
              <option value="false">Røres ikke</option>
            </select>
          </label>
        </div>

        {message && (
          <div
            className={message.ok ? 'small' : 'error-box'}
            style={{ marginBottom: 10, color: message.ok ? 'var(--success)' : undefined }}
          >
            {message.text}
          </div>
        )}

        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>
            Gem indstillinger
          </button>
          <button
            className="btn"
            type="button"
            disabled={sync.isPending || !settings.isSyncConfigured}
            onClick={() => void runNow()}
          >
            {sync.isPending ? 'Synkroniserer ...' : 'Synkronisér nu'}
          </button>
        </div>
      </form>

      {result && <SyncResultBox result={result} />}
    </Card>
  )
}

/**
 * Hvor filerne ligger: teamet, hvis SharePoint-bibliotek de gemmes i, og hvad
 * sagsmapperne skal hedde (docs/DECISIONS.md beslutning 033 og 034).
 */
function FilesCard({ settings }: { settings: Microsoft365Settings }) {
  const save = useSaveMicrosoft365Settings()
  const testFiles = useTestFileAccess()

  const [form, setForm] = useState({
    teamsTeamId: settings.teamsTeamId ?? '',
    filesFolderNamePattern: settings.filesFolderNamePattern ?? ''
  })
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setMessage(null)
    try {
      await save.mutateAsync({ ...settings, ...form })
      setMessage({ ok: true, text: 'Indstillingerne er gemt.' })
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Kunne ikke gemme.' })
    }
  }

  return (
    <Card title="Filer og mapper">
      <p className="small muted" style={{ marginTop: 0 }}>
        Filer, fotos og servicedokumentation gemmes i teamets SharePoint-bibliotek - aldrig på
        serveren. Systemet opretter selv mapperne:{' '}
        <code>Sager/&lt;sag&gt;/Billeder + Dokumenter</code> og{' '}
        <code>Service/&lt;enhed&gt;/Billeder + Dokumenter</code>.
        Kræver rettigheden <code>Sites.Selected</code> med administratorsamtykke og en
        site-tildeling.
      </p>

      <form onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Team-id (object id, tom = filer slået fra)</span>
            <input
              value={form.teamsTeamId}
              placeholder="f.eks. teamet flow-TC"
              onChange={(e) => setForm({ ...form, teamsTeamId: e.target.value })}
            />
          </label>

          <label className="field">
            <span className="field__label">Navn på sagsmappen</span>
            <input
              value={form.filesFolderNamePattern}
              placeholder="{adresse} {postnr} {by} ({ordre})"
              onChange={(e) => setForm({ ...form, filesFolderNamePattern: e.target.value })}
            />
            <span className="tiny muted">
              Pladsholdere: {'{adresse}'} (vej og nr.), {'{postnr}'}, {'{by}'}, {'{ordre}'}, {'{navn}'}, {'{kunde}'} og{' '}
              {'{id}'}. Adressen er der, hvor arbejdet foregår - ellers kundens. Uden adresse bruges sagens navn.
              Navnet forkortes til 50 tegn, men ordrenummeret bliver stående. Gælder kun nye sager - de gamle beholder
              deres mappe.
            </span>
          </label>

        </div>

        {message && (
          <div
            className={message.ok ? 'small' : 'error-box'}
            style={{ marginBottom: 10, color: message.ok ? 'var(--success)' : undefined }}
          >
            {message.text}
          </div>
        )}

        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>
            Gem indstillinger
          </button>
          <button
            className="btn"
            type="button"
            disabled={testFiles.isPending || !settings.teamsTeamId}
            onClick={() => testFiles.mutate()}
          >
            {testFiles.isPending ? 'Tester ...' : 'Test filadgang'}
          </button>
        </div>
      </form>

      {testFiles.data && (
        <div
          className={testFiles.data.success ? 'small' : 'error-box'}
          style={{ marginTop: 12, color: testFiles.data.success ? 'var(--success)' : undefined }}
        >
          {testFiles.data.message}
        </div>
      )}

      <p className="tiny muted" style={{ marginBottom: 0 }}>
        <code>Sites.Selected</code> giver i sig selv <strong>ingen</strong> adgang - rettigheden
        skal tildeles pr. site med to kald i Graph Explorer (docs/INSTALL.md afsnit 1g). Det er
        det trin, der oftest mangler, når "Test filadgang" fejler.
      </p>
    </Card>
  )
}

/**
 * Udsendelse af mail fra systemet (beslutning 038).
 *
 * Mailen sendes med den samme appregistrering som brugersynkroniseringen - derfor
 * står kortet her og ikke på en side for sig. Afsenderen er én fast postkasse,
 * ikke den enkelte medarbejder: systemet skriver på virksomhedens vegne.
 */
function MailCard() {
  const queryClient = useQueryClient()
  const { data } = useQuery({
    queryKey: ['mail-settings'],
    queryFn: () => api.get<MailSettings>('/api/settings/mail')
  })

  const [from, setFrom] = useState('')
  const [replyTo, setReplyTo] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [testTo, setTestTo] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    if (!data) return
    setFrom(data.fromAddress ?? '')
    setReplyTo(data.replyTo ?? '')
    setEnabled(data.enabled)
  }, [data])

  async function run(action: () => Promise<{ ok: boolean; text: string }>) {
    setBusy(true)
    setMessage(null)
    try {
      setMessage(await action())
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Handlingen mislykkedes.' })
    } finally {
      setBusy(false)
    }
  }

  if (!data) return null

  return (
    <Card title="Mail">
      <p className="small muted" style={{ marginTop: 0 }}>
        Systemet sender mail gennem Microsoft 365 med den samme appregistrering som herover.
        Afsenderen skal være en rigtig postkasse - et alias eller en distributionsgruppe kan ikke
        sende.
      </p>

      {!data.graphReady && (
        <div className="error-box" style={{ marginBottom: 12 }}>
          Microsoft 365 mangler tenant, klient-id eller klienthemmelighed. Uden dem kan der ikke
          sendes mail, uanset hvad der står herunder.
        </div>
      )}

      <div className="form-grid">
        <label className="field">
          <span className="field__label">Afsender</span>
          <input
            type="email"
            placeholder="noreply@tc-el.dk"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">Svar går til (valgfri)</span>
          <input
            type="email"
            placeholder="kontakt@tc-el.dk"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
          />
        </label>
      </div>

      <p className="tiny muted" style={{ marginTop: 0 }}>
        En noreply-postkasse bliver sjældent læst. Skriv en rigtig adresse i "Svar går til", hvis
        modtageren skal kunne svare.
      </p>

      <label className="row" style={{ gap: 8, marginTop: 6 }}>
        {/* width: auto - ellers fylder feltet hele bredden og skubber teksten væk. */}
        <input
          type="checkbox"
          style={{ width: 'auto' }}
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <span>Send mail fra systemet</span>
      </label>

      <p className="tiny muted" style={{ marginTop: 6 }}>
        Står den fra, sendes der intet. En mail forlader huset i virksomhedens navn, så det skal
        slås til bevidst.
      </p>

      <div className="btn-group" style={{ marginTop: 12 }}>
        <button
          className="btn btn--primary btn--sm"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              await api.put('/api/settings/mail', { fromAddress: from, replyTo, enabled })
              await queryClient.invalidateQueries({ queryKey: ['mail-settings'] })
              return { ok: true, text: 'Opsætningen er gemt.' }
            })
          }
        >
          Gem
        </button>
      </div>

      {data.isConfigured && (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
          <div className="form-grid">
            <label className="field">
              <span className="field__label">Send en testmail til</span>
              <input
                type="email"
                placeholder="din@mail.dk"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
              />
            </label>
          </div>
          <button
            className="btn btn--sm"
            disabled={busy || !testTo.trim()}
            onClick={() =>
              void run(async () => {
                const result = await api.post<{ success: boolean; message: string }>(
                  '/api/settings/mail/test',
                  { to: testTo.trim() }
                )
                return { ok: result.success, text: result.message }
              })
            }
          >
            Send testmail
          </button>
        </div>
      )}

      {message && (
        <div
          className={message.ok ? 'small' : 'error-box'}
          style={{ marginTop: 12, color: message.ok ? 'var(--success)' : undefined }}
        >
          {message.text}
        </div>
      )}
    </Card>
  )
}

function SyncResultBox({ result }: { result: DirectorySyncResult }) {
  return (
    <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
      <div className="row row--between">
        <strong className="small">Seneste synkronisering</strong>
        <span className="tiny muted">
          {formatDateTime(result.ranAt)} · {result.triggeredBy}
        </span>
      </div>

      {result.success ? (
        <div className="row" style={{ flexWrap: 'wrap', marginTop: 8 }}>
          <span className="pill" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
            {result.totalInDirectory} i Microsoft 365
          </span>
          <span className="pill" style={{ background: '#DCFCE7', color: '#166534' }}>{result.created} oprettet</span>
          <span className="pill" style={{ background: '#F1F5F9', color: '#334155' }}>{result.updated} opdateret</span>
          <span className="pill" style={{ background: '#FEE2E2', color: '#991B1B' }}>{result.deactivated} deaktiveret</span>
          {result.reactivated > 0 && (
            <span className="pill" style={{ background: '#DCFCE7', color: '#166534' }}>{result.reactivated} genaktiveret</span>
          )}
        </div>
      ) : (
        <div className="error-box" style={{ marginTop: 8 }}>{result.error}</div>
      )}

      {result.warnings.length > 0 && (
        <ul className="tiny muted" style={{ marginTop: 8, paddingLeft: 18 }}>
          {result.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

function RoleMappingCard() {
  const { data, isLoading } = useRoles()

  return (
    <Card title="Roller fra Entra ID-grupper">
      <p className="small muted" style={{ marginTop: 0 }}>
        Kobles en rolle til en Entra ID-gruppe, tildeles rollen automatisk ved login ud fra gruppemedlemskab.
        Manuelt tildelte roller påvirkes ikke.
      </p>

      {isLoading && <Loading />}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Rolle</th>
              <th>Entra-gruppe (object id)</th>
              <th>Brugere</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((role) => (
              <RoleRow key={role.id} role={role} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function RoleRow({ role }: { role: Role }) {
  const setGroup = useSetRoleEntraGroup(role.id)
  const [value, setValue] = useState(role.entraGroupId ?? '')
  const [saved, setSaved] = useState(false)

  return (
    <tr>
      <td>{role.name}</td>
      <td>
        <div className="row">
          <input
            value={value}
            placeholder="Ingen kobling"
            onChange={(e) => {
              setValue(e.target.value)
              setSaved(false)
            }}
          />
          <button
            className="btn btn--sm"
            disabled={setGroup.isPending || value === (role.entraGroupId ?? '')}
            onClick={async () => {
              await setGroup.mutateAsync(value.trim() || null)
              setSaved(true)
            }}
          >
            Gem
          </button>
          {saved && <span className="tiny" style={{ color: 'var(--success)' }}>Gemt</span>}
        </div>
      </td>
      <td className="small muted">{role.userCount}</td>
    </tr>
  )
}
